import { useMemo, useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import TextField from '@mui/material/TextField';
import Button from '../Button';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormControl from '@mui/material/FormControl';
import PropTypes from 'prop-types';
import { isEmpty, isEqual } from 'lodash';
import Address from './address';
import DatePicker from 'components/DatePicker';
import translating from '../../translate.json';
import { Switch, TextareaAutosize } from '@mui/material';
import './style.scss';
import { RichTextEditor } from '../RichTextEditor';

const FormGroup = ({ fields, data, onSubmit }) => {
  const {
    handleSubmit,
    register,
    control,
    watch,
    setError,
    setValue,
    clearErrors,
    getValues,
    formState: { errors }
  } = useForm({
    defaultValues: useMemo(() => {
      if (data) {
        const deepData = JSON.parse(JSON.stringify(data));
        if (deepData.date_of_birth) {
          let d = deepData.date_of_birth.split('.');
          deepData.date_of_birth = `${d?.[1]}.${d?.[0]}.${d?.[2]}`;
        }
        return deepData;
      }
    }, [data])
  });
  const [submitedValues, setSubmitedValues] = useState({});
  const handleClickSubmit = (data) => {
    if (errors.apiError) {
      clearErrors('apiError');
    }
    setSubmitedValues(data);
    if (isEmpty(errors)) {
      onSubmit(data, setError);
      // reset(data)
    }
  };

  useEffect(() => {
    if (!isEqual(submitedValues, getValues()))
      if (errors.apiError) {
        clearErrors();
      }
  }, [getValues()]);

  const onEditorStateChange = (editorState, fieldName) => {
    setValue(fieldName, editorState);
  };

  useEffect(() => {
    if (fields[0]?.name === 'summery')
      setValue(fields[0]?.name, fields[0]?.default);
  }, [fields[0]?.default]);

  return (
    <form className="form-group">
      <div className="form-group-wrapper-inputs">
        {fields?.map((field, index) => (
          <>
            {field.breakLine && <div className="break"></div>}
            <div
              key={index}
              className={`form-group-wrapper-input`}
              style={{ width: field.width }}
            >
              <InputLabel classes={{ root: 'rootLabel' }}>
                {field.label}
              </InputLabel>
              {(() => {
                switch (field?.type) {
                  case 'address':
                    return (
                      <Address
                        Controller={Controller}
                        control={control}
                        field={field}
                        dataField={data?.[field?.name]}
                        errors={errors}
                      ></Address>
                    );
                  case 'date':
                    return (
                      <div className={`${errors?.[field.name] ? 'error' : ''}`}>
                        <DatePicker
                          Controller={Controller}
                          control={control}
                          field={field}
                          {...register(field.name, {
                            required: field.required
                              ? translating.requiredMsg
                              : false
                          })}
                        ></DatePicker>
                      </div>
                    );
                  case 'select':
                    return (
                      <Select
                        defaultValue={data?.[field?.name] || field.default}
                        {...register(field.name, {
                          required: field.required
                            ? translating.requiredMsg
                            : false
                        })}
                        classes={{
                          select: 'selectRoot',
                          icon: 'selectIcon'
                        }}
                      >
                        {field?.options?.map((option, key) => (
                          <MenuItem key={key} value={option.id}>
                            {option.name}
                          </MenuItem>
                        ))}
                      </Select>
                    );
                  case 'radio':
                    return (
                      <FormControl>
                        <RadioGroup
                          classes={{ root: 'radioRoot' }}
                          defaultValue={
                            data?.[field?.name]?.[field?.subField] ||
                            data?.[field?.name] ||
                            field.default
                          }
                          className={`${field.flex ? 'radio-column' : ''}`}
                        >
                          {field?.options?.map((option, index) => (
                            <FormControlLabel
                              key={index}
                              control={
                                <Radio
                                  {...register(field.name, {
                                    required: field.required
                                      ? translating.requiredMsg
                                      : false
                                  })}
                                  value={field?.byId ? option.id : option.name}
                                />
                              }
                              label={option.name}
                            />
                          ))}
                        </RadioGroup>
                      </FormControl>
                    );
                  case 'switch': {
                    let value = data?.[field?.name];
                    return (
                      <Switch
                        defaultChecked={
                          value
                            ? value
                            : typeof value === 'undefined'
                            ? field?.default
                            : false
                        }
                        label={field?.label}
                        {...register(field.name)}
                      />
                    );
                  }
                  case 'textarea':
                    return (
                      <RichTextEditor
                        {...register(field.name)}
                        value={watch(field.name)}
                        className="editor"
                        onChange={(value) =>
                          onEditorStateChange(value, field.name)
                        }
                      />
                    );
                  case 'simple-textarea':
                    return (
                      <TextareaAutosize
                        minRows={field.rows || 4}
                        {...register(field.name, {
                          required: field.required
                            ? translating.requiredMsg
                            : false,
                          pattern: field.pattern,
                          validate: field.validate
                        })}
                        placeholder={field.placeholder}
                        maxLength={500}
                        fullWidth
                      />
                    );
                  default:
                    return (
                      <TextField
                        value={data?.[field?.name]?.[field?.subField]}
                        defaultValue={field.default}
                        fullWidth
                        required={field.required}
                        InputProps={{
                          inputProps: { min: 0 },
                          readOnly: field.readOnly
                        }}
                        type={field?.type || 'text'}
                        className={`${errors?.[field.name] ? 'error' : ''}`}
                        classes={{ root: 'rootInput' }}
                        {...register(field.name, {
                          required: field.required
                            ? translating.requiredMsg
                            : false,
                          pattern: field.pattern
                        })}
                      />
                    );
                }
              })()}
              {errors?.[field.name] && (
                <p className="err-msg">
                  {`${
                    field.required && errors?.[field?.name]?.type === 'required'
                      ? field.label
                      : ''
                  } ${errors?.[field.name]?.message}`}
                </p>
              )}
            </div>
          </>
        ))}
      </div>
      {errors['apiError'] && (
        <p className="err-msg">{errors['apiError']?.message}</p>
      )}
      <div className="wrapper-button">
        <Button
          classes={{ root: 'submit-btn' }}
          variant="outlined"
          disableElevation
          handleClick={handleSubmit(handleClickSubmit)}
        >
          שמור
        </Button>
      </div>
    </form>
  );
};

export default FormGroup;

FormGroup.propTypes = {
  fields: PropTypes.array,
  data: PropTypes.object,
  onSubmit: PropTypes.func
};
